/**
 * Dialog para crear nuevo MediaReport.
 * Selección: kind (detallado/resumen), format (xlsx/pdf/pptx), cliente, rango fechas.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { media, type ReportKind, type ReportFormat } from "@/lib/api";

export function ReportCreateDialog() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<ReportKind>("detallado");
  const [format, setFormat] = useState<ReportFormat>("xlsx");
  const [clientId, setClientId] = useState<string>("");
  const [campaignId, setCampaignId] = useState<string>("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const clientsQ = useQuery({
    queryKey: ["media", "clients"],
    queryFn: () => media.listClients({ limit: 200 }),
  });
  const campaignsQ = useQuery({
    queryKey: ["media", "campaigns", "picker", clientId || null],
    queryFn: () => media.listCampaigns({ client_id: clientId ? Number(clientId) : undefined, limit: 300 }),
  });

  const create = useMutation({
    mutationFn: () => media.createReport({
      name,
      kind,
      format: kind === "detallado" ? "xlsx" : format,
      client_id: clientId ? Number(clientId) : undefined,
      filters: {
        ...(dateFrom ? { date_from: dateFrom } : {}),
        ...(dateTo ? { date_to: dateTo } : {}),
        ...(clientId ? { client_id: Number(clientId) } : {}),
        ...(campaignId ? { campaign_id: Number(campaignId) } : {}),
      },
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "reports"] });
      toast.success("Reporte creado");
      setOpen(false);
      setName(""); setClientId(""); setCampaignId(""); setDateFrom(""); setDateTo("");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus size={14} /> Nuevo reporte
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Crear reporte</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Nombre *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Reporte SEAL Junio 2026" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Tipo</Label>
              <Select value={kind} onValueChange={(v) => setKind(v as ReportKind)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="detallado">Detallado (Excel)</SelectItem>
                  <SelectItem value="resumen">Resumen</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Formato</Label>
              <Select value={format} onValueChange={(v) => setFormat(v as ReportFormat)} disabled={kind === "detallado"}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="xlsx">Excel (XLSX)</SelectItem>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="pptx">PowerPoint (PPTX)</SelectItem>
                </SelectContent>
              </Select>
              {kind === "detallado" && (
                <p className="text-[10px] text-ink-400 mt-1">Detallado solo en XLSX</p>
              )}
            </div>
          </div>
          <div>
            <Label className="text-xs">Cliente (opcional — todos si vacío)</Label>
            <Select value={clientId} onValueChange={(v) => { setClientId(v); setCampaignId(""); }}>
              <SelectTrigger><SelectValue placeholder="Todos los clientes" /></SelectTrigger>
              <SelectContent>
                {(clientsQ.data ?? []).map(c => (
                  <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Campaña (opcional)</Label>
            <Select value={campaignId || "all"} onValueChange={(v) => setCampaignId(v === "all" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="Todas las campañas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las campañas</SelectItem>
                {(campaignsQ.data ?? []).map(c => (
                  <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Desde</Label>
              <DatePicker value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Hasta</Label>
              <DatePicker value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={() => create.mutate()} disabled={!name || create.isPending}>
            Crear
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
